import { Fragment, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog, InlineName, itemNames, MoveButtons, OpenButton, reorderPatches, TD, TH } from "@/components/admin/menu/shared";

/**
 * Menu groups: the level above a category (Chinese, Tandoor, Bar Menu…). The
 * bill's "group" sectioning and the group reports read them; until now they could
 * only be created by an import. Each group sits under a master group (Food /
 * Beverages / Extras), which is what the "group" bill layout prints.
 *
 * Each row opens to show what is in the group — its categories and their items —
 * and categories can be added or taken out right there (floor report 2026-10-19:
 * "you let me edit the name from outside but don't let me see what's inside").
 */

// Radix Select cannot hold "" as an item value.
const NO_MASTER = "none";
export default function MenuGroupsTab({ data, readOnly }) {
  const { categories, groups, masters } = data;
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [openId, setOpenId] = useState(null);

  const rows = [...(groups || [])].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const categoryCount = groupId => (categories || []).filter(category => category.group_id === groupId).length;

  const add = async event => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    const result = await data.createGroup({ name: name.trim(), sort_order: (rows.length + 1) * 10 });
    setBusy(false);
    if (!result.ok) return toast.error(`Could not add group: ${result.error}`);
    toast.success(`Group "${name.trim()}" added`);
    setName("");
  };

  const rename = async (group, next) => {
    const result = await data.updateGroup(group.id, { name: next });
    if (!result.ok) toast.error(`Could not rename "${group.name}": ${result.error}`);
    else toast.success(`Renamed to "${next}"`);
    return result;
  };

  const setMaster = async (group, value) => {
    const result = await data.updateGroup(group.id, { group_category_id: value === NO_MASTER ? null : Number(value) });
    if (!result.ok) toast.error(`Could not change the master group of "${group.name}": ${result.error}`);
  };

  const move = async (index, delta) => {
    setBusy(true);
    const result = await data.reorderGroups(reorderPatches(rows, index, delta));
    setBusy(false);
    if (!result.ok) toast.error(`Could not reorder: ${result.error}`);
  };

  const setCategoryGroup = async (category, groupId) => {
    const result = await data.updateCategory(category.id, { group_id: groupId });
    if (!result.ok) toast.error(`Could not move "${category.name}": ${result.error}`);
    return result;
  };

  const setActive = async (group, active) => {
    const result = active
      ? await data.updateGroup(group.id, { is_active: true })
      : await data.deactivateGroup(group.id);
    if (!result.ok) return toast.error(`Could not ${active ? "reactivate" : "deactivate"} "${group.name}": ${result.error}`);
    toast.success(active ? `"${group.name}" reactivated` : `"${group.name}" deactivated`);
  };

  if (!groups) return <p className="px-3 py-6 text-meta text-muted-foreground">Loading groups…</p>;

  return (
    <div className="space-y-3" data-testid="menu-groups-tab">
      {!readOnly && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={add}>
          <label className="flex flex-col gap-1 text-meta font-medium">
            New group
            <Input className="h-8 w-56 text-body" value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Bar Menu" data-testid="menu-group-new-name" />
          </label>
          <Button variant="action" type="submit" size="sm" disabled={busy || !name.trim()} data-testid="menu-group-add">
            <Plus className="h-4 w-4" />Add Group
          </Button>
        </form>
      )}

      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className={TH}>Name</th>
              <th className={TH}>Master group</th>
              <th className={`${TH} text-right`}>Categories</th>
              <th className={TH}>Order</th>
              <th className={TH}>Active</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((group, index) => (
              <Fragment key={group.id}>
              <tr className={group.is_active ? "" : "bg-muted/30"} data-testid={`menu-group-row-${group.id}`}>
                <td className={TD}>
                  <span className="flex items-center gap-1">
                    <OpenButton open={openId === group.id} onClick={() => setOpenId(openId === group.id ? null : group.id)} label={`What is in ${group.name}`} testId={`menu-group-open-${group.id}`} />
                    <InlineName value={group.name} disabled={readOnly} label={`Name of ${group.name}`} onCommit={next => rename(group, next)} testId={`menu-group-name-${group.id}`} />
                  </span>
                </td>
                <td className={TD}>
                  <Select
                    value={group.group_category_id == null ? NO_MASTER : String(group.group_category_id)}
                    onValueChange={value => setMaster(group, value)}
                    disabled={readOnly}
                  >
                    <SelectTrigger className="h-8 w-44 text-body" aria-label={`Master group of ${group.name}`} data-testid={`menu-group-master-${group.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_MASTER}>No master group</SelectItem>
                      {(masters || []).filter(master => master.is_active || master.id === group.group_category_id).map(master => (
                        <SelectItem key={master.id} value={String(master.id)}>{master.name}{master.is_active ? "" : " (inactive)"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className={`${TD} text-right tabular-nums text-muted-foreground`}>{categoryCount(group.id)}</td>
                <td className={TD}>
                  <MoveButtons index={index} count={rows.length} onMove={move} disabled={readOnly || busy} testIdPrefix={`menu-group-move-${group.id}`} />
                </td>
                <td className={TD}>
                  <Switch
                    checked={group.is_active !== false}
                    disabled={readOnly}
                    aria-label={`${group.name} active`}
                    onCheckedChange={next => (next ? setActive(group, true) : setConfirm(group))}
                    data-testid={`menu-group-active-${group.id}`}
                  />
                </td>
              </tr>
              {openId === group.id && (
                <tr data-testid={`menu-group-contents-${group.id}`}>
                  <td colSpan={5} className="bg-muted/20 px-3 py-2">
                    <GroupContents group={group} categories={categories || []} readOnly={readOnly} onMove={setCategoryGroup} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-meta text-muted-foreground">No groups yet. Add the first one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? `Deactivate "${confirm.name}"?` : ""}
        body={confirm ? `Its ${categoryCount(confirm.id)} categories stay on the menu, but the group is no longer offered when assigning one. Switch it back on here at any time.` : ""}
        confirmLabel="Deactivate Group"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const target = confirm; setConfirm(null); setActive(target, false); }}
        testId="menu-group-deactivate-dialog"
      />
    </div>
  );
}

/** A group's categories with their items, and the controls to add or take one out. */
function GroupContents({ group, categories, readOnly, onMove }) {
  const inside = categories.filter(category => category.group_id === group.id);
  const others = categories.filter(category => category.group_id !== group.id && category.is_active !== false);
  return (
    <div className="space-y-1.5">
      {inside.length === 0 && <p className="text-meta text-muted-foreground">No categories in {group.name} yet.</p>}
      {inside.map(category => (
        <div key={category.id} className="flex items-start gap-2 text-body" data-testid={`menu-group-${group.id}-category-${category.id}`}>
          <span className="min-w-0 flex-1">
            <span className="font-medium">{category.name}</span>
            <span className="text-meta text-muted-foreground"> · {(category.items || []).length} items{(category.items || []).length ? `: ${itemNames(category)}` : ""}</span>
          </span>
          {!readOnly && (
            <button type="button" className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-meta text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => onMove(category, null)} data-testid={`menu-group-${group.id}-remove-${category.id}`}>
              <X className="h-3.5 w-3.5" />Take out
            </button>
          )}
        </div>
      ))}
      {!readOnly && others.length > 0 && (
        <Select value="" onValueChange={value => { const category = others.find(c => String(c.id) === value); if (category) onMove(category, group.id); }}>
          <SelectTrigger className="h-8 w-64 text-body" aria-label={`Add a category to ${group.name}`} data-testid={`menu-group-${group.id}-add-category`}>
            <SelectValue placeholder="Add a category…" />
          </SelectTrigger>
          <SelectContent>
            {others.map(category => (
              <SelectItem key={category.id} value={String(category.id)}>
                {category.name}{category.group_id == null ? " (no group)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
